import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { ReactiveFormsModule, FormGroup, FormControl } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { RouterModule } from '@angular/router';
import { UserInfoInterface } from '@core/models/authentication.model';
import { AuthenticationService } from '@core/services/authentication/authentication.service';
import { TRANSACTION_STATUS } from '@feature/dashboard/transactions/constant/transaction-list.constant';
import {
  updateTransactionListFilter,
  resetTransactionList,
} from '@feature/dashboard/transactions/store/transaction-list.actions';
import { selectTransactionList } from '@feature/dashboard/transactions/store/transaction-list.selector';
import { TransactionListInterface } from '@feature/dashboard/transactions/types/transaction-list';
import {
  defaultTransactionListFilter,
  TransactionListFilter,
} from '@feature/dashboard/transactions/types/transaction-list-filter';
import { Store } from '@ngrx/store';
import { CustomDateRangeHeaderComponent } from '@sharedModule/components/custom-date-range-header/custom-date-range-header.component';
import { InputModule } from '@sharedModule/components/input/input.module';
import { sortDirection } from '@sharedModule/constants/data-grid.constants';
import { ATTRIBUTES_CONSTANT } from '@sharedModule/constants/fields.constant';
import { MaterialModule } from '@sharedModule/material/material.module';
import { ExcelExportService } from '@sharedModule/services/excel-export/excel-export.service';
import { getActiveSort } from '@sharedModule/utils/sort.utils';
import { Subject, debounceTime, takeUntil } from 'rxjs';

@Component({
  selector: 'app-sender-transaction-list',
  standalone: true,
  imports: [
    InputModule,
    CommonModule,
    RouterModule,
    MaterialModule,
    ReactiveFormsModule,
  ],
  templateUrl: './sender-transaction-list.component.html',
  styleUrl: './sender-transaction-list.component.scss',
})
export class SenderTransactionListComponent implements OnInit, OnDestroy {
  @Input() id!: string;
  @Input() isSuperAdmin!: boolean;
  public readonly attrConstant = ATTRIBUTES_CONSTANT.transactions;
  public readonly CustomDateRangeHeader = CustomDateRangeHeaderComponent;
  senderId!: number;
  partnerId?: number;
  filterForm!: FormGroup;
  transactionStatus = TRANSACTION_STATUS;
  displayedColumns: string[] = [
    'trans_session_id',
    'created_at',
    'transaction_status',
    'source_amount',
    'destination_amount',
    'beneficiary_first_name',
  ];
  transactionList?: TransactionListInterface[];
  dataSource!: MatTableDataSource<TransactionListInterface>;
  activeSort?: Sort;
  fallBackSort = {
    active: 'created_at',
    direction: sortDirection.desc,
  };
  page = 0;
  total = 0;
  limit = 10;
  subject = new Subject<{
    search: string;
    status: string;
    from_date: string;
    to_date: string;
  }>();
  userInfo?: UserInfoInterface;
  #destroy$ = new Subject<void>();

  constructor(
    private store: Store,
    private excelExportService: ExcelExportService,
    private authenticationService: AuthenticationService
  ) {
    this.userInfo = this.authenticationService.getUserInfo();
  }

  ngOnInit(): void {
    if (this.isSuperAdmin) {
      this.senderId = +this.id.split('-')[0];
      this.partnerId = +this.id.split('-')[1];
    } else {
      this.senderId = +this.id;
    }
    this.filterFormInit();
    this.loadData();
    this.dispatchFilter();
    const debounceTimeDuration = 1000;
    this.subject
      .pipe(debounceTime(debounceTimeDuration), takeUntil(this.#destroy$))
      .subscribe(res => {
        this.dispatchFilter({
          page: 0,
          count: 10,
          ...res,
          partner_id: this.partnerId ? [this.partnerId] : null,
          current_status: [this.filterForm.value[this.attrConstant.status.key]],
        });
      });
  }

  filterFormInit(): void {
    this.filterForm = new FormGroup({
      [this.attrConstant.search.key]: new FormControl(''),
      [this.attrConstant.status.key]: new FormControl(''),
      [this.attrConstant.from_date.key]: new FormControl(''),
      [this.attrConstant.to_date.key]: new FormControl(''),
    });
  }

  loadData(): void {
    this.store
      .select(selectTransactionList)
      .pipe(takeUntil(this.#destroy$))
      .subscribe(data => {
        if (data) {
          this.transactionList = data.rawData;
          this.dataSource = new MatTableDataSource(data.rawData);
          this.page = data.page;
          this.total = data.totalRecords;
          this.limit = data.count;
        }
      });
  }

  applyFilter(): void {
    this.subject.next(this.filterForm.value);
  }

  clearFilters(): void {
    this.filterForm.reset();
    this.dispatchFilter({
      ...defaultTransactionListFilter,
      partner_id: this.partnerId ? [this.partnerId] : null,
      sender_id: [this.senderId],
    });
  }

  reload(): void {
    this.dispatchFilter();
  }

  onPageChange(pageEvent: PageEvent): void {
    this.dispatchFilter({
      count: pageEvent.pageSize,
      page: pageEvent.pageIndex,
    });
  }

  onSortChange(sortEvent: Sort): void {
    this.activeSort = getActiveSort(sortEvent, this.fallBackSort);
    this.dispatchFilter({
      sort_key: this.activeSort.active,
      sort_by: this.activeSort.direction,
    });
  }

  dispatchFilter(transactionListFilter?: TransactionListFilter): void {
    this.store.dispatch(
      updateTransactionListFilter({
        filter: {
          ...transactionListFilter,
          partner_id: this.partnerId ? [this.partnerId] : null,
          sender_id: [this.senderId],
        },
      })
    );
  }

  export(): void {
    this.excelExportService.exportToExcel(
      this.transactionList!,
      'transactions'
    );
  }

  ngOnDestroy(): void {
    this.store.dispatch(resetTransactionList());
    this.#destroy$.next();
    this.#destroy$.complete();
  }
}
